import React from 'react';
import clsx from 'clsx';
import type {SetOptional} from 'type-fest';
import {RecognitionFaceAvatar} from '@netx/core-recognition-face/components/RecognitionFaceAvatar';
import {
	ListItem,
	ListItemAvatar,
	type ListItemProps,
	ListItemText,
} from '@mui/material';
import {
	composeClasses,
	styled,
	type WithClasses,
	type WithOwnerState,
} from '@netx/core-components/Theme';
import {PersonStatus, type IPerson} from '../../schema';
import {
	getPersonListItemUtilityClass,
	PERSON_ITEM_CLASS_NAME,
	type PersonListItemClasses,
	personListItemClasses,
} from './personListItemClasses';

type PersonListItemOwnerState = WithClasses<
	PersonListItemClasses,
	{
		isActive?: boolean;
	}
>;

const useUtilityClasses = (state: PersonListItemOwnerState) => {
	const {classes, isActive} = state;
	const slots = {
		root: ['root', isActive && 'isActive'],
	};
	return composeClasses(slots, getPersonListItemUtilityClass, classes);
};

const PersonListItemRoot = styled(ListItem, {
	name: PERSON_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}: WithOwnerState<PersonListItemOwnerState>) => ({
	// ...(ownerState?.hasSecondaryLabel ? {minHeight: 56} : {}),
	[`&.${personListItemClasses.isActive}`]: {
		backgroundColor: theme.palette.action.hover,
	},
}));

export interface PersonListItemProps
	extends
		Omit<ListItemProps, 'classes' | 'sx'>,
		WithClasses<PersonListItemClasses> {
	isActive?: boolean;
	person: SetOptional<IPerson, 'avatarUrl' | 'assetCount'>;
	secondaryAction?: React.ReactNode;
}

export const PersonListItemPrimitive = (
	{
		classes: _classes,
		className,
		isActive,
		person,
		secondaryAction,
		...props
	}: PersonListItemProps,
	ref: React.ForwardedRef<HTMLLIElement>,
) => {
	const ownerState: PersonListItemOwnerState = {
		classes: _classes,
		className,
		isActive,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<PersonListItemRoot
			className={clsx(classes.root, className)}
			ref={ref}
			{...props}
			secondaryAction={secondaryAction}
			ownerState={ownerState}
		>
			<ListItemAvatar>
				<RecognitionFaceAvatar
					alt={person.name ?? 'placeholder'}
					avatarUrl={person.avatarUrl}
					isNew={person.id === 'new'}
				/>
				{/* {person.avatarUrl ? (
					<Avatar alt={person.name ?? 'placeholder'} src={person.avatarUrl} />
				) : person.id !== 'new' ? (
					<Avatar>
						<FontAwesomeIcon icon={faQuestion} />
					</Avatar>
				) : (
					<Avatar>
						<FontAwesomeIcon icon={faPlus} />
					</Avatar>
				)} */}
			</ListItemAvatar>
			<ListItemText
				primary={
					person.status === PersonStatus.UNIDENTIFIED
						? i18n.t('i18n.unidentified')
						: person.name || i18n.t('i18n.unidentified')
				}
				secondary={
					person.assetCount == null
						? undefined
						: i18n.t('i18n.nAssets', {count: person.assetCount})
				}
			/>
		</PersonListItemRoot>
	);
};

export const PersonListItem = React.forwardRef(PersonListItemPrimitive);

export default PersonListItem;
